import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { EditorContent, useEditor } from '@tiptap/react';
import { ySyncPluginKey } from '@tiptap/y-tiptap';
import type { Awareness } from 'y-protocols/awareness';
import type * as Y from 'yjs';
import type { SceneStore } from '@os/doc';
import { CANVAS_UI } from '../editor/canvas-ui';
import { useObject } from '../editor/useObject';
import { FormatBar } from './FormatBar';
import { createPortal } from 'react-dom';
import { useCoarsePointer, useLayout } from '../../app/layout';
import { richExtensions, type CaretUser } from './extensions';
import { graphOf } from '../editor/graph-of';
import { stickyInk } from '../../ui/palette';

type Props = {
  store: SceneStore;
  id: string;
  onDone: () => void;
  awareness?: Awareness;
  user?: CaretUser;
  /** Content height in world px (tests inject it: happy-dom has no layout). */
  measure?: (el: HTMLElement) => number;
};

const fragmentKeys = new WeakMap<Y.XmlFragment, number>();
let nextKey = 1;
const keyOf = (f: Y.XmlFragment) =>
  fragmentKeys.get(f) ?? (fragmentKeys.set(f, nextKey++), nextKey - 1);

/**
 * In-place rich-text editing of a text, sticky or shape, over the object in world coordinates. Bound to the object's
 * fragment (re-created if a peer replaced it); writes go through y-tiptap, undo through the board's single stack.
 */
export function RichTextEditor(props: Props) {
  const { store, id } = props;
  const [fragment] = useState(() => store.commands.ensureContent(id) ?? store.fragment(id));
  const current = useSyncExternalStore(
    (cb) => store.subscribe(id, cb),
    () => store.fragment(id) ?? fragment,
  );
  if (!current) return null;
  return <BoundEditor key={keyOf(current)} {...props} fragment={current} />;
}

function BoundEditor({
  store,
  id,
  onDone,
  awareness,
  user,
  measure,
  fragment,
}: Props & { fragment: Y.XmlFragment }) {
  const { t } = useTranslation();
  const o = useObject(store, id);
  const readOnly = useSyncExternalStore(
    (cb) => store.subscribeReadOnly(cb),
    () => store.readOnly,
  );
  const box = useRef<HTMLDivElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  // Phones: the bar leaves the zoomed world for the bottom of the screen (React events still bubble through the portal).
  // Phones, and any finger: 44 px controls never fit in a bar floating above the text (tablet, P16-2).
  const coarse = useCoarsePointer();
  const docked = useLayout() === 'phone' || coarse;
  const done = useRef(false);
  const finish = () => {
    if (done.current) return;
    done.current = true;
    const el = box.current;
    const cur = store.getObject(id);
    const w = store.graph().world.get(id);
    if (el && cur?.type === 'text' && w && !store.readOnly) {
      const h = Math.ceil((measure ?? ((e: HTMLElement) => e.scrollHeight))(el));
      if (h > cur.h) store.commands.resizeObject(id, { x: w.x, y: w.y, w: w.w, h });
    }
    onDone();
  };
  const finishRef = useRef(finish);
  finishRef.current = finish;

  useEffect(() => store.trackOrigin(ySyncPluginKey), [store]);
  const editor = useEditor(
    {
      extensions: richExtensions({ fragment, undoManager: store.undoManager, awareness, user }),
      editable: !store.readOnly,
      autofocus: 'end',
      immediatelyRender: true,
      editorProps: {
        attributes: {
          role: 'textbox',
          'aria-multiline': 'true',
          'aria-label': t('editor.editText'),
        },
        handleKeyDown: (_view, e) => {
          if (e.key !== 'Escape') return false;
          e.stopPropagation();
          finishRef.current();
          return true;
        },
      },
      onBlur: ({ event }) => {
        // Clicks in the format bar keep editing.
        if (event.relatedTarget instanceof Node && bar.current?.contains(event.relatedTarget))
          return;
        finishRef.current();
      },
    },
    [fragment],
  );
  useEffect(() => editor?.setEditable(!readOnly), [editor, readOnly]);

  const w = graphOf(store, o).world.get(id); // `o` keeps the React Compiler from caching a stale box
  if (!o || !w || (o.type !== 'text' && o.type !== 'sticky' && o.type !== 'shape')) return null;
  const style =
    o.type === 'text'
      ? {
          fontSize: o.fontSize,
          lineHeight: 1.3,
          color: typeof o.style.color === 'string' ? o.style.color : undefined,
        }
      : o.type === 'sticky'
        ? { color: stickyInk(o.color) }
        : undefined;
  const look =
    o.type === 'sticky'
      ? 'p-3 text-base'
      : o.type === 'shape'
        ? 'flex items-center justify-center p-2 text-center text-base text-[#141414]'
        : 'text-[var(--canvas-ink,#141414)]';
  return (
    <div
      className="absolute left-0 top-0"
      style={{
        transform: `translate(${w.x}px, ${w.y}px) rotate(${w.rotation}deg)`,
        width: w.w,
        minHeight: w.h,
      }}
      {...{ [CANVAS_UI]: '' }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => {
        // Clicks on the bar (buttons or its padding) keep the editor focused and its selection.
        if (
          e.target instanceof Node &&
          bar.current?.contains(e.target) &&
          !(e.target instanceof HTMLInputElement)
        )
          e.preventDefault();
      }}
    >
      {!readOnly &&
        editor &&
        (docked ? (
          createPortal(<FormatBar ref={bar} editor={editor} docked />, document.body)
        ) : (
          <FormatBar ref={bar} editor={editor} />
        ))}
      <div
        ref={box}
        className={`rich font-sans outline-2 outline-accent ${look}`}
        style={{ ...style, minHeight: w.h, background: o.type === 'sticky' ? o.color : undefined }}
      >
        <EditorContent editor={editor} className="h-full w-full" />
      </div>
    </div>
  );
}
